import { styled } from '@mui/material/styles';
import { Paper, Typography, Box, Tooltip, IconButton } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import Contact from './Contact';
import { ClientContact } from '../../../data/models/client.model';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientContacts';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  contacts: `${PREFIX}-contacts`,
  addContactButton: `${PREFIX}-addContactButton`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiTypography-root': {
    color: `${theme.palette.lightTextPrimary.main} !important`,
  },

  [`&.${classes.container}`]: {
    flex: 1,
    minWidth: 300,
  },

  [`& .${classes.details}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    padding: theme.spacing(3, 4),
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    padding: 0,
  },

  [`& .${classes.contacts}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 4, 3, 4),
  },

  [`& .${classes.addContactButton}`]: {
    padding: 0,
  },
}));

interface ClientContactsProps {
  clientContacts?: ClientContact[];
  addContact: () => void;
  editContact: (contact: ClientContact) => void;
  activateContact: (contact: ClientContact) => void;
  deactivateContact: (contact: ClientContact) => void;
  deleteContact: (contact: ClientContact) => void;
  readonly: boolean;
}

export default function ClientContacts({
  clientContacts,
  addContact,
  editContact,
  activateContact,
  deactivateContact,
  deleteContact,
  readonly,
}: ClientContactsProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t('client.contacts')}
          </Typography>
          {!readonly && !isSuperReaderUser && (
            <Tooltip title={t('client.addContact')}>
              <span>
                <IconButton
                  data-testid="add-address"
                  onClick={addContact}
                  className={classes.addContactButton}
                  color="primary"
                  size="large">
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        {!!clientContacts?.length && (
          <Box className={classes.contacts}>
            {clientContacts?.map((contact) => (
              <Contact
                clientContact={contact}
                key={contact.clientContactUUID}
                editContact={editContact}
                activateContact={activateContact}
                deactivateContact={deactivateContact}
                deleteContact={deleteContact}
                readonly={readonly}
              />
            ))}
          </Box>
        )}
      </Paper>
    </StyledBox>
  );
}
